import { useEffect, useState } from 'react';
import { FormControl } from '@mui/material';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { useBazanGroupFields } from '../../../hooks/useBazanGroupFields';
import { Select, TSelectOption } from '@/components/common/SelectField';
import { BazanFieldKey, BazanPriorityValues } from '../../../enums/bazan';
import { Field, FieldLabel } from '@/components/ui/field';
import { FieldsGroup } from '../../ui/FieldsGroup';
import { BazanPriorityIcon } from '../../ui/PriorityIcon';

// const truncateText = (text, maxLength) => {
//   return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
// };

const fieldLabels = {
  [BazanFieldKey.FunctionalLocation]: 'Functional Location',
  [BazanFieldKey.Equipment]: 'Equipment number',
  [BazanFieldKey.NoteType]: 'Note Type',
  [BazanFieldKey.EquipmentFailure]: 'Equipment Failure',
  [BazanFieldKey.FaultSource]: 'Fault Source',
  [BazanFieldKey.Priority]: 'Priority',
};

const priorityDataMap: Record<BazanPriorityValues, { endDate: number }> = {
  [BazanPriorityValues.Highest]: { endDate: 1 }, // 1 day
  [BazanPriorityValues.High]: { endDate: 3 }, // 3 days
  [BazanPriorityValues.Medium]: { endDate: 21 }, // 3 weeks
  [BazanPriorityValues.Low]: { endDate: 56 }, // 8 weeks
};

export type SAPField = { key: string; options: Array<{ value: string; label: string }> };

type Props = {
  setSAPData: (data: any) => void;
  SAPFieldsData: Array<SAPField>;
  SAPData: any;
};

export const SAPNoteForm = ({ setSAPData, SAPData, SAPFieldsData }: Props) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [filteredEquipment, setFilteredEquipment] = useState([]);

  useEffect(() => {
    if (Object.keys(SAPData).length < 2 && SAPFieldsData.length > 0) {
      const initialSAPData = {};
      SAPFieldsData.forEach((field) => {
        // This is intiailizing in note form
        if (field.key === BazanFieldKey.NoteType) return;

        const serverKey = field.key;
        initialSAPData[serverKey] = field.options[0]?.value || '';
        if (serverKey === BazanFieldKey.FunctionalLocation) {
          setSelectedLocation(field.options[0]?.value);
        }
      });

      setSAPData((prev) => ({ ...prev, ...initialSAPData }));
    }
  }, [SAPFieldsData, SAPData, setSAPData]);

  useEffect(() => {
    const equipmentField = SAPFieldsData.find((field) => field.key === BazanFieldKey.Equipment);
    if (equipmentField && selectedLocation) {
      const equipmentOptions = equipmentField.options[selectedLocation] || [];
      setFilteredEquipment(equipmentOptions);
    }
  }, [selectedLocation, SAPFieldsData]);

  useEffect(() => {
    const calculateEndDate = () => {
      if (SAPData?.priority) {
        const daysToAdd = priorityDataMap[SAPData?.priority as BazanPriorityValues]?.endDate || 0;
        return moment().add(daysToAdd, 'days').toDate();
      }
      return null;
    };

    const newEndDate = calculateEndDate();

    if (newEndDate) {
      setSelectedDate(newEndDate);
      setSAPData((prevData) => ({
        ...prevData,
        endDate: moment(newEndDate).format('YYYYMMDD HHmm'),
      }));
    }
  }, [SAPData?.priority, setSAPData]);

  const handleFieldChange =
    (fieldKey: string) =>
    ({ value }: TSelectOption) => {
      if (fieldKey === BazanFieldKey.FunctionalLocation) {
        setSelectedLocation(value);
      }

      setSAPData((prevData) => ({
        ...prevData,
        [fieldKey]: value,
      }));
    };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSAPData((prevData) => ({
      ...prevData,
      endDate: moment(date).format('YYYYMMDD HHmm'),
    }));
  };

  const groupedFields = useBazanGroupFields(SAPFieldsData);

  const formatPriorityLabel = (option: TSelectOption): TSelectOption => {
    return {
      ...option,
      label: (
        <>
          <BazanPriorityIcon priority={option.value as BazanPriorityValues} />
          {option.label}
        </>
      ),
    };
  };

  return (
    <>
      {groupedFields.details?.length && (
        <FieldsGroup label="Details" tooltip="details" defaultExpanded>
          {groupedFields.details.map((field) => {
            const label = fieldLabels[field.key] || field.key;

            const options =
              field.key === BazanFieldKey.Equipment ? filteredEquipment : field.options;

            return (
              <Field key={field.key}>
                <FieldLabel>{label}</FieldLabel>
                <Select
                  value={SAPData[field.key] || ''}
                  onChange={handleFieldChange(field.key)}
                  options={options}
                />
              </Field>
            );
          })}
        </FieldsGroup>
      )}

      {groupedFields['task-info']?.length && (
        <FieldsGroup label="Task Info" tooltip="task info" defaultExpanded>
          {groupedFields['task-info'].map((field) => {
            const label = fieldLabels[field.key] || field.key;

            const options =
              field.key === BazanFieldKey.Priority
                ? field.options.map(formatPriorityLabel)
                : field.options;

            return (
              <Field key={field.key}>
                <FieldLabel>{label}</FieldLabel>
                <Select
                  value={SAPData[field.key] || ''}
                  onChange={handleFieldChange(field.key)}
                  options={options}
                />
              </Field>
            );
          })}

          <Field>
            <FieldLabel>Due Date</FieldLabel>
            <DatePicker
              enableTabLoop={false} // with tabLoop  enabled it adds unnecessary space under the trigger
              selected={selectedDate}
              onChange={handleDateChange}
              dateFormat="dd-MM-yyyy HH:mm"
              customInput={
                <FormControl fullWidth>
                  <Select
                    value={selectedDate ? moment(selectedDate).format('DD.MM.YYYY') : ''}
                    options={[]}
                    asTrigger
                    helperIcon="fa-regular fa-calendar"
                  />
                </FormControl>
              }
            />
          </Field>
        </FieldsGroup>
      )}
    </>
  );
};
